import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import type { ConsentCopySummary } from "../../../shared/ui/consent/ConsentCopiesList";
import type { ConsentDocument } from "../domain/types";
import { PortalAPI } from "../infrastructure/portalApi";
import { putConsentFile } from "../infrastructure/consentUpload";

/**
 * The patient's consent in the portal: the current document and its rendered
 * PDF, the signed copies, and the ways to give (accept, sign, upload) or
 * withdraw it.
 */
export function usePortalConsent() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [doc, setDoc] = useState<ConsentDocument | null>(null);
    const [loading, setLoading] = useState(true);
    const [acknowledged, setAcknowledged] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showRevoke, setShowRevoke] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [documentUrl, setDocumentUrl] = useState<string | null>(null);
    const [documentFailed, setDocumentFailed] = useState(false);

    const [hasSignature, setHasSignature] = useState(false);

    const [copies, setCopies] = useState<ConsentCopySummary[]>([]);
    const [downloadingCopyId, setDownloadingCopyId] = useState<string | null>(null);

    const loadCopies = () => {
        PortalAPI.getConsentCopies()
            .then(({ data }) => setCopies(data))
            .catch(() => setCopies([]));
    };

    useEffect(loadCopies, []);

    // The document is fetched and framed from a blob rather than linked: the
    // portal's requests carry a bearer token an <iframe> would not send, and the
    // patient must read the very text the signature is recorded against.
    useEffect(() => {
        let objectUrl: string | null = null;
        PortalAPI.getCurrentConsentPdf()
            .then(({ data }) => {
                objectUrl = URL.createObjectURL(data);
                setDocumentUrl(objectUrl);
            })
            .catch(() => setDocumentFailed(true));
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, []);

    useEffect(() => {
        PortalAPI.getCurrentConsent()
            .then(({ data }) => {
                setDoc(data);
                setLoading(false);
                if (!data.requires_acceptance) {
                    setAcknowledged(true);
                }
            })
            .catch(() => setLoading(false));
    }, []);

    const accept = async () => {
        if (!doc) return;
        setSubmitting(true);
        setError(null);
        try {
            await PortalAPI.acceptConsent(doc.id);
            navigate("/portal", { replace: true });
        } catch {
            setError(t("portal.consent.acceptError"));
        } finally {
            setSubmitting(false);
        }
    };

    /** `signatureImage` is the pad's drawing as a data URL, if anything was drawn. */
    const sign = async (signatureImage: string | null | undefined) => {
        if (!doc || !signatureImage) return;
        setSubmitting(true);
        setError(null);
        try {
            await PortalAPI.signConsent(doc.id, signatureImage);
            navigate("/portal", { replace: true });
        } catch {
            setError(t("portal.consent.signError"));
        } finally {
            setSubmitting(false);
        }
    };

    const downloadCopy = async (copyId: string) => {
        setDownloadingCopyId(copyId);
        try {
            const { data } = await PortalAPI.getConsentCopyUrl(copyId);
            window.open(data.download_url, "_blank", "noopener,noreferrer");
        } catch {
            // Silent: the list only offers copies the server just listed, and a
            // failed link is not something the patient can act on.
        } finally {
            setDownloadingCopyId(null);
        }
    };

    const upload = async () => {
        if (!selectedFile) return;
        setUploading(true);
        setUploadError(null);
        setUploadSuccess(false);
        try {
            const { data: { upload_url, blob_name } } = await PortalAPI.getConsentUploadUrl(selectedFile.type);
            await putConsentFile(upload_url, selectedFile);
            await PortalAPI.confirmConsentUpload(blob_name);
            setUploadSuccess(true);
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            const { data } = await PortalAPI.getCurrentConsent();
            setDoc(data);
            loadCopies();
        } catch {
            setUploadError(t("portal.consent.uploadError"));
        } finally {
            setUploading(false);
        }
    };

    const revoke = async () => {
        setSubmitting(true);
        setError(null);
        try {
            await PortalAPI.revokeConsent();
            const { data } = await PortalAPI.getCurrentConsent();
            setDoc(data);
            setAcknowledged(false);
            setShowRevoke(false);
        } catch {
            setError(t("portal.consent.revokeError"));
        } finally {
            setSubmitting(false);
        }
    };

    return {
        doc,
        loading,
        acknowledged,
        setAcknowledged,
        submitting,
        showRevoke,
        setShowRevoke,
        error,
        selectedFile,
        setSelectedFile,
        uploading,
        uploadSuccess,
        setUploadSuccess,
        uploadError,
        setUploadError,
        fileInputRef,
        documentUrl,
        documentFailed,
        hasSignature,
        setHasSignature,
        copies,
        downloadingCopyId,
        accept,
        sign,
        downloadCopy,
        upload,
        revoke,
    };
}
